import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  WifiOff,
  ArrowLeft,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { motion } from 'framer-motion';
import ErrorTable from '../../../../inventory/components/bulk-stock-upload/ErrorTable';
import { usePurchaseOrderCsvUpload } from '../usePurchaseOrderCsvUpload';

interface ResultStepProps {
  onUploadAnother: () => void;
  onClose: () => void;
  onGoBack: () => void;
}

const ResultStep: React.FC<ResultStepProps> = ({
  onUploadAnother,
  onClose,
  onGoBack,
}) => {
  const { result } = usePurchaseOrderCsvUpload();
  const [errorsOpen, setErrorsOpen] = useState(false);

  if (!result) return null;

  const { successCount, failedCount, validationErrors, executionErrors } = result;
  const totalErrors = validationErrors.length + executionErrors.length;
  const isFullSuccess = failedCount === 0 && totalErrors === 0;

  // Merge execution errors into the validation error format for display
  const allErrors = [
    ...validationErrors,
    ...executionErrors.map((msg) => ({
      row: 0,
      sku: '--',
      reason: msg,
    })),
  ];

  // --- Blocked: validation errors, nothing applied ---
  if (result.mode === 'blocked') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-3 py-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <ShieldAlert className="h-12 w-12 text-red-500" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-center"
          >
            <h3 className="text-lg font-semibold">Upload Blocked</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {failedCount} validation {failedCount === 1 ? 'error' : 'errors'} found. Nothing was applied.
            </p>
          </motion.div>
        </div>
        <div className="flex items-center justify-center">
          <Badge variant="destructive" size="lg">
            {failedCount} {failedCount === 1 ? 'error' : 'errors'} to fix
          </Badge>
        </div>
        {allErrors.length > 0 && <ErrorTable errors={allErrors} />}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            onClick={onGoBack}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90 gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Go Back &amp; Fix
          </Button>
        </div>
      </div>
    );
  }

  // --- Network error: typically won't happen for PO apply (no backend call) ---
  if (result.mode === 'network_error') {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-3 py-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            <WifiOff className="h-12 w-12 text-amber-500" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-center max-w-sm"
          >
            <h3 className="text-lg font-semibold">Connection Lost</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Something went wrong while loading SKU data. Please try again.
            </p>
          </motion.div>
        </div>
        <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <AlertDescription className="text-amber-800 dark:text-amber-300 text-sm">
            Close this dialog and retry the CSV upload once your connection is restored.
          </AlertDescription>
        </Alert>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            onClick={onUploadAnother}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Success / partial failure header */}
      <div className="flex flex-col items-center gap-3 py-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        >
          {isFullSuccess ? (
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          ) : (
            <AlertTriangle className="h-12 w-12 text-amber-500" />
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-center"
        >
          <h3 className="text-lg font-semibold">
            {isFullSuccess ? 'Purchase Order Items Applied' : 'Applied with Errors'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {successCount} SKU{successCount !== 1 ? 's' : ''} added to your draft order.
          </p>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-center gap-3">
        <Badge variant="success" size="lg">
          {successCount} applied
        </Badge>
        {failedCount > 0 && (
          <Badge variant="destructive" size="lg">
            {failedCount} skipped
          </Badge>
        )}
      </div>

      {/* Errors section */}
      {allErrors.length > 0 && (
        <Collapsible open={errorsOpen} onOpenChange={setErrorsOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-between px-3"
            >
              <span className="text-sm">
                {errorsOpen ? 'Hide' : 'Show'} {allErrors.length} skipped row
                {allErrors.length !== 1 ? 's' : ''}
              </span>
              {errorsOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <ErrorTable errors={allErrors} />
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onUploadAnother}>
          Upload Another
        </Button>
        <Button
          onClick={onClose}
          className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default ResultStep;
